import React from 'react';

interface AuthStepIndicatorProps {
  /** 1-based index of the step the user is on. Clamped into [1, total]. */
  current: number;
  /** Total number of steps. Values below 1 are treated as 1. */
  total: number;
  /** Optional name of the current step, shown next to the "STEP n OF m" line. */
  label?: string;
  /** Optional wrapper classes (spacing is left to the host). */
  className?: string;
}

/**
 * Sign-up progress for the 400px auth column.
 *
 * Deliberately NOT `pages/courses/wizard/WizardStepIndicator` — that one is
 * sticky, capped at max-w-[890px], hard-codes pixel step positions and uses
 * text-xl labels, none of which survive a 375px viewport inside a 400px form.
 *
 * `role="progressbar"` sits on the bar only: `progressbar` is a
 * presentational-children role, so text nested inside it would be dropped by
 * assistive tech. Keeping the "STEP n OF m" line as a sibling means the count
 * and the progress value are both announced.
 */
const AuthStepIndicator: React.FC<AuthStepIndicatorProps> = ({
  current,
  total,
  label,
  className = '',
}) => {
  const safeTotal = Math.max(1, Math.floor(total));
  const safeCurrent = Math.min(safeTotal, Math.max(1, Math.floor(current)));
  const segments = Array.from({ length: safeTotal }, (_, index) => index);
  const valueText = label
    ? `Step ${safeCurrent} of ${safeTotal}: ${label}`
    : `Step ${safeCurrent} of ${safeTotal}`;

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="m-0 shrink-0 text-[11px] font-bold leading-4 tracking-[0.08em] text-text-secondary">
          STEP {safeCurrent} OF {safeTotal}
        </p>
        {label && (
          <p className="m-0 min-w-0 truncate text-xs font-semibold leading-4 text-primary-500">
            {label}
          </p>
        )}
      </div>

      <div
        role="progressbar"
        aria-label="Sign-up progress"
        aria-valuemin={1}
        aria-valuemax={safeTotal}
        aria-valuenow={safeCurrent}
        aria-valuetext={valueText}
        className="mt-2 flex items-center gap-1.5"
      >
        {segments.map((index) => {
          const filled = index < safeCurrent;
          return (
            <span
              key={index}
              data-testid="auth-step-segment"
              data-state={filled ? 'filled' : 'empty'}
              className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ease-out-strong ${
                filled ? 'bg-primary-500' : 'bg-primary-100 dark:bg-primary-900/40'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};

export default AuthStepIndicator;
